export interface ServerEvent {
  /** The `event:` name, or "message" when the server sent none. */
  event: string
  /** The `data:` lines joined with newlines. */
  data: string
}

/**
 * Reads a server-sent event stream: frames separated by a blank line, each with optional `event:` and one or more `data:` lines
 * (lines starting with `:` are comments and ignored). Ends when the server closes the stream or the reader is cancelled.
 */
export async function* readEventStream(response: Response): AsyncGenerator<ServerEvent> {
  if (!response.body) return
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  const parse = (frame: string): ServerEvent | null => {
    let event = 'message'
    const data: string[] = []
    for (const line of frame.split(/\r?\n/)) {
      if (!line || line.startsWith(':')) continue
      const colon = line.indexOf(':')
      const field = colon === -1 ? line : line.slice(0, colon)
      const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '')
      if (field === 'event') event = value
      else if (field === 'data') data.push(value)
    }
    return data.length ? { event, data: data.join('\n') } : null
  }
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let boundary = buffer.search(/\r?\n\r?\n/)
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary)
        buffer = buffer.slice(boundary).replace(/^\r?\n\r?\n/, '')
        const parsed = parse(frame)
        if (parsed) yield parsed
        boundary = buffer.search(/\r?\n\r?\n/)
      }
    }
    const last = parse(buffer)
    if (last) yield last
  } finally {
    reader.releaseLock()
  }
}
